import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { gsap } from 'gsap';
// Hardcoded layout data with mixed hover effects
const STATIC_SHAPES = [
    // Move effects (8 shapes)
    {
        type: 'diamond',
        color: '#A8EEF5',
        position: [-1.3, 0.8],
        size: 0.25,
        rotation: 0.5,
        hoverEffect: 'move',
        hover: { x: 0.05, y: -0.08 },
    },
    {
        type: 'circle',
        color: '#FEF08A',
        position: [0.5, 0.6],
        size: 0.2,
        rotation: 0,
        hoverEffect: 'move',
        hover: { x: 0.1, y: 0.1 },
    },
    {
        type: 'triangle', // Replaced from zigzag
        color: '#34D399',
        position: [-1.2, -0.8], // Moved to bottom-left
        size: 0.28,
        rotation: 4.5,
        hoverEffect: 'move',
        hover: { x: 0.07, y: -0.07 },
    },
    {
        type: 'halfCircle',
        color: '#22D3EE',
        position: [1.4, 0.5],
        size: 0.3,
        rotation: 1.6,
        hoverEffect: 'move',
        hover: { x: 0, y: 0.1 },
    },
    {
        type: 'circle',
        color: '#FECDD3', // Swapped color from #A7F3D0
        position: [-1.1, 0.1],
        size: 0.28,
        rotation: 0,
        hoverEffect: 'move',
        hover: { x: -0.05, y: 0.1 },
    },
    {
        type: 'triangle',
        color: '#f9fafb',
        position: [0.6, -0.4],
        size: 0.25,
        rotation: 5.5,
        hoverEffect: 'move',
        hover: { x: 0.09, y: 0.06 },
    },
    {
        type: 'square',
        color: '#A7F3D0', // Swapped color from #FECDD3
        position: [1.2, -0.1],
        size: 0.2,
        rotation: 0.3,
        hoverEffect: 'move',
        hover: { x: 0.1, y: 0 },
    },
    {
        type: 'circle',
        color: '#E0F8FA',
        position: [0.2, 1.0],
        size: 0.15,
        rotation: 0,
        hoverEffect: 'move',
        hover: { x: 0, y: -0.1 },
    },
    // Rotate effects (7 shapes)
    {
        type: 'diamond', // Replaced from wave
        color: '#FB923C',
        position: [1.1, -0.7],
        size: 0.3,
        rotation: 1.2,
        hoverEffect: 'rotate',
        hover: { rotation: Math.PI / 6 },
    },
    {
        type: 'triangle',
        color: '#F43F5E',
        position: [-0.8, -0.5],
        size: 0.35,
        rotation: 2.1,
        hoverEffect: 'rotate',
        hover: { rotation: -Math.PI / 4 },
    },
    {
        type: 'square',
        color: '#e5e7eb',
        position: [-0.2, 0.7],
        size: 0.18,
        rotation: 3.8,
        hoverEffect: 'rotate',
        hover: { rotation: Math.PI / 3 },
    },
    {
        type: 'diamond',
        color: '#FFF6E5',
        position: [0.1, -0.9],
        size: 0.22,
        rotation: 5.0,
        hoverEffect: 'rotate',
        hover: { rotation: -Math.PI / 5 },
    },
    {
        type: 'diamond', // Replaced from wave
        color: '#FEF8C5',
        position: [-0.5, 0.2],
        size: 0.2,
        rotation: 0.8,
        hoverEffect: 'rotate',
        hover: { rotation: Math.PI / 2 },
    },
    {
        type: 'triangle', // Replaced from zigzag
        color: '#FDE4B8',
        position: [-1.5, -0.8],
        size: 0.32,
        rotation: 2.9,
        hoverEffect: 'rotate',
        hover: { rotation: -Math.PI / 7 },
    },
    {
        type: 'halfCircle',
        color: '#F0F0E8',
        position: [-0.1, -0.2],
        size: 0.3,
        rotation: 4.0,
        hoverEffect: 'rotate',
        hover: { rotation: Math.PI },
    },
];
const createMemphisGeometries = () => {
    const diamondShape = new THREE.Shape();
    diamondShape.moveTo(0, 0.5);
    diamondShape.lineTo(0.35, 0);
    diamondShape.lineTo(0, -0.5);
    diamondShape.lineTo(-0.35, 0);
    diamondShape.lineTo(0, 0.5);
    return {
        square: new THREE.PlaneGeometry(1, 1),
        circle: new THREE.CircleGeometry(0.5, 32),
        triangle: new THREE.ConeGeometry(0.5, 1, 3),
        diamond: new THREE.ShapeGeometry(diamondShape),
        halfCircle: new THREE.CircleGeometry(0.5, 32, 0, Math.PI),
    };
};
const PixelBlast = ({ className, style, }) => {
    const containerRef = useRef(null);
    const animationFrameId = useRef(null);
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        container.appendChild(renderer.domElement);
        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
        camera.position.z = 1;
        const geometries = createMemphisGeometries();
        const objects = [];
        const setSize = () => {
            const w = container.clientWidth;
            const h = container.clientHeight;
            renderer.setSize(w, h, false);
            const aspect = w / h;
            camera.left = -aspect;
            camera.right = aspect;
            camera.updateProjectionMatrix();
        };
        setSize();
        STATIC_SHAPES.forEach(shapeData => {
            const geometry = geometries[shapeData.type];
            const material = new THREE.MeshBasicMaterial({ color: shapeData.color });
            const object = new THREE.Mesh(geometry, material);
            const [x, y] = shapeData.position;
            object.position.set(x, y, 0);
            object.rotation.z = shapeData.rotation;
            object.scale.set(shapeData.size, shapeData.size, shapeData.size);
            object.userData = {
                isAnimating: false, // New flag to prevent re-triggering
                hoverEffect: shapeData.hoverEffect,
                originalPosition: { x, y },
                targetPosition: {
                    x: x + (shapeData.hoverEffect === 'move' ? (shapeData.hover.x ?? 0) : 0),
                    y: y + (shapeData.hoverEffect === 'move' ? (shapeData.hover.y ?? 0) : 0),
                },
                originalRotation: shapeData.rotation,
                targetRotation: shapeData.rotation +
                    (shapeData.hoverEffect === 'rotate' ? (shapeData.hover.rotation ?? 0) : 0),
            };
            scene.add(object);
            objects.push(object);
        });
        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2(-999, -999);
        const onMouseMove = (event) => {
            const rect = renderer.domElement.getBoundingClientRect();
            mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        };
        const animate = () => {
            animationFrameId.current = requestAnimationFrame(animate);
            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObjects(objects);
            if (intersects.length > 0) {
                const obj = intersects[0].object;
                if (!obj.userData.isAnimating) {
                    obj.userData.isAnimating = true;
                    const tl = gsap.timeline({
                        onComplete: () => {
                            obj.userData.isAnimating = false;
                        },
                    });
                    if (obj.userData.hoverEffect === 'move') {
                        tl.to(obj.position, {
                            x: obj.userData.targetPosition.x,
                            y: obj.userData.targetPosition.y,
                            duration: 0.4,
                            ease: 'power2.out',
                        }).to(obj.position, {
                            x: obj.userData.originalPosition.x,
                            y: obj.userData.originalPosition.y,
                            duration: 0.6,
                            ease: 'elastic.out(1, 0.75)',
                        });
                    }
                    else if (obj.userData.hoverEffect === 'rotate') {
                        tl.to(obj.rotation, {
                            z: obj.userData.targetRotation,
                            duration: 0.4,
                            ease: 'power2.out',
                        }).to(obj.rotation, {
                            z: obj.userData.originalRotation,
                            duration: 0.6,
                            ease: 'elastic.out(1, 0.75)',
                        });
                    }
                }
            }
            renderer.render(scene, camera);
        };
        animate();
        window.addEventListener('mousemove', onMouseMove);
        const resizeObserver = new ResizeObserver(setSize);
        resizeObserver.observe(container);
        return () => {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
            window.removeEventListener('mousemove', onMouseMove);
            resizeObserver.disconnect();
            if (container && renderer.domElement.parentElement === container) {
                container.removeChild(renderer.domElement);
            }
            // Kill all tweens to prevent memory leaks
            gsap.globalTimeline.clear();
            Object.values(geometries).forEach(g => g.dispose());
            renderer.dispose();
        };
    }, []);
    return (_jsx("div", { ref: containerRef, className: `w-full h-full absolute top-0 left-0 -z-10 overflow-hidden ${className ?? ''}`, style: { pointerEvents: 'none', ...style }, "aria-label": "Interactive Memphis background" }));
};
export default PixelBlast;
//# sourceMappingURL=PixelBlast.js.map